/**
 * ProxyBuilder - Fluent API for configuring per-method CallOptions
 *
 * Provides a builder pattern for creating native proxies with method-specific
 * call options, enabling fine-grained control over timeouts and retry strategies.
 *
 * @example
 * ```typescript
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000, maxRetries: 2 })
 *   .configureMethod('generateReport', { timeout: 60000, maxRetries: 1 })
 *   .configureMethod('quickCheck', { timeout: 1000, maxRetries: 0 })
 *   .build();
 * ```
 */

import { CallOptions } from "../types/protocol";
import { ProxyOptions } from "./NativeProxyManager";
import { BifrostWeb } from "../BifrostWeb";

/**
 * Configuration mapping for method-specific call options.
 *
 * Keys are method names, values are CallOptions to apply when that method is called.
 *
 * @example
 * ```typescript
 * const methodOptions: MethodConfiguration = {
 *   'slowMethod': { timeout: 30000, maxRetries: 1 },
 *   'quickMethod': { timeout: 1000, maxRetries: 0 }
 * };
 * ```
 */
export interface MethodConfiguration {
  [methodName: string]: CallOptions;
}

/**
 * Internal configuration state for the ProxyBuilder.
 *
 * Tracks the service name, proxy-level defaults, method-specific options,
 * and additional proxy configuration.
 */
interface BuilderConfiguration {
  /** The name of the service being configured */
  serviceName: string;

  /** Default call options for all methods on this proxy (overrides global, can be overridden per method) */
  defaultOptions?: CallOptions;

  /** Method-specific call options (overrides proxy defaults, can be overridden at call time) */
  methodOptions: MethodConfiguration;

  /** Additional proxy options (validation, caching, etc.) */
  proxyOptions: ProxyOptions;
}

/**
 * ProxyBuilder provides a fluent API for configuring native proxies with
 * per-method call options.
 *
 * Supports four-tier option precedence:
 * - Global (BifrostConfig.defaultCallOptions)
 * - Proxy (ProxyBuilder.withDefaultOptions)
 * - Method (ProxyBuilder.configureMethod)
 * - Call (per-call options parameter)
 *
 * Priority: global < proxy < method < call (rightmost wins)
 *
 * @template T - The service interface type
 *
 * @example
 * ```typescript
 * interface UserService {
 *   getProfile(userId: number): Promise<UserProfile>;
 *   generateReport(params: ReportParams): Promise<Report>;
 * }
 *
 * const userService = bifrost
 *   .createProxyBuilder<UserService>('UserService')
 *   .withDefaultOptions({ timeout: 5000 })
 *   .configureMethod('generateReport', { timeout: 60000 })
 *   .build();
 * ```
 */
export class ProxyBuilder<T = any> {
  private config: BuilderConfiguration;
  private isBuilt = false;

  /**
   * Creates a new ProxyBuilder instance.
   *
   * @param bifrostWeb - The BifrostWeb instance to create the proxy from
   * @param serviceName - Name of the service to create a proxy for
   *
   * @throws Error if service name is invalid
   */
  constructor(
    private bifrostWeb: BifrostWeb,
    serviceName: string,
  ) {
    this.validateServiceName(serviceName);

    this.config = {
      serviceName,
      methodOptions: {},
      proxyOptions: {},
    };
  }

  /**
   * Set default options for all methods on this proxy.
   *
   * These options override global defaults but can be overridden by
   * method-specific configuration or call-level options.
   *
   * Can be called multiple times; last call wins.
   *
   * @param options - Default call options for all methods
   * @returns This builder instance for method chaining
   *
   * @example
   * ```typescript
   * builder.withDefaultOptions({ timeout: 5000, maxRetries: 2 });
   * ```
   */
  withDefaultOptions(options: CallOptions): ProxyBuilder<T> {
    this.throwIfBuilt();
    this.validateOptions(options);
    this.config.defaultOptions = options;
    return this;
  }

  /**
   * Configure call options for a specific method.
   *
   * These options override proxy defaults but can be overridden at call time.
   *
   * Can be called multiple times for the same method; last call wins.
   *
   * @param methodName - Name of the method to configure
   * @param options - Call options for this method
   * @returns This builder instance for method chaining
   *
   * @throws Error if method name is invalid
   * @throws Error if options are invalid
   *
   * @example
   * ```typescript
   * builder
   *   .configureMethod('slowMethod', { timeout: 30000, maxRetries: 1 })
   *   .configureMethod('quickMethod', { timeout: 1000, maxRetries: 0 });
   * ```
   */
  configureMethod(methodName: string, options: CallOptions): ProxyBuilder<T> {
    this.throwIfBuilt();
    this.validateMethodName(methodName);
    this.validateOptions(options);
    this.config.methodOptions[methodName] = options;
    return this;
  }

  /**
   * Build the configured proxy.
   *
   * Can only be called once per builder instance. After building, the builder
   * is locked and cannot be modified.
   *
   * @returns A typed proxy for the service with configured options
   *
   * @throws Error if builder has already been built
   *
   * @example
   * ```typescript
   * const service = builder
   *   .withDefaultOptions({ timeout: 5000 })
   *   .configureMethod('slowMethod', { timeout: 30000 })
   *   .build();
   * ```
   */
  build(): T {
    this.throwIfBuilt();
    this.isBuilt = true;

    // Prepare proxy options with method-specific configuration
    const proxyOptions: ProxyOptions = {
      ...this.config.proxyOptions,
      defaultCallOptions: this.config.defaultOptions,
      methodOptions: this.config.methodOptions,
    };

    // Create proxy using NativeProxyManager
    return this.bifrostWeb.createNativeProxy<T>(
      this.config.serviceName,
      proxyOptions,
    );
  }

  /**
   * Get the current configuration for debugging purposes.
   *
   * Returns a readonly copy of the builder's configuration state.
   *
   * @returns Readonly configuration object
   *
   * @example
   * ```typescript
   * const config = builder.getConfiguration();
   * console.log('Service:', config.serviceName);
   * console.log('Default options:', config.defaultOptions);
   * console.log('Method options:', config.methodOptions);
   * ```
   */
  getConfiguration(): Readonly<BuilderConfiguration> {
    return {
      serviceName: this.config.serviceName,
      defaultOptions: this.config.defaultOptions,
      methodOptions: { ...this.config.methodOptions },
      proxyOptions: { ...this.config.proxyOptions },
    };
  }

  /**
   * Reset the builder configuration to initial state.
   *
   * Clears all default options and method-specific configurations.
   * Does not reset the service name.
   *
   * @returns This builder instance for method chaining
   *
   * @throws Error if builder has already been built
   *
   * @example
   * ```typescript
   * builder
   *   .withDefaultOptions({ timeout: 5000 })
   *   .configureMethod('slowMethod', { timeout: 30000 })
   *   .reset()  // Clear all configuration
   *   .withDefaultOptions({ timeout: 3000 });
   * ```
   */
  reset(): ProxyBuilder<T> {
    this.throwIfBuilt();
    this.config.defaultOptions = undefined;
    this.config.methodOptions = {};
    return this;
  }

  /**
   * Validate service name format.
   *
   * Service names must:
   * - Not be empty
   * - Start with a letter
   * - Contain only letters, numbers, and underscores
   *
   * @param serviceName - Service name to validate
   * @throws Error if service name is invalid
   */
  private validateServiceName(serviceName: string): void {
    if (!serviceName || typeof serviceName !== "string") {
      throw new Error(`Invalid service name: ${serviceName}`);
    }

    const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    if (!validNamePattern.test(serviceName)) {
      throw new Error(`Invalid service name: ${serviceName}`);
    }
  }

  /**
   * Validate method name format.
   *
   * Method names must:
   * - Not be empty
   * - Start with a letter
   * - Contain only letters, numbers, and underscores
   *
   * @param methodName - Method name to validate
   * @throws Error if method name is invalid
   */
  private validateMethodName(methodName: string): void {
    if (!methodName || typeof methodName !== "string") {
      throw new Error(`Invalid method name: ${methodName}`);
    }

    const validNamePattern = /^[a-zA-Z][a-zA-Z0-9_]*$/;
    if (!validNamePattern.test(methodName)) {
      throw new Error(`Invalid method name: ${methodName}`);
    }
  }

  /**
   * Validate CallOptions structure.
   *
   * Ensures that:
   * - timeout is positive if specified
   * - maxRetries is non-negative if specified
   *
   * @param options - Options to validate
   * @throws Error if options are invalid
   */
  private validateOptions(options: CallOptions): void {
    if (options.timeout !== undefined) {
      if (typeof options.timeout !== "number" || options.timeout < 0) {
        throw new Error(
          `Invalid timeout: ${options.timeout}. Must be a positive number.`,
        );
      }
    }

    if (options.maxRetries !== undefined) {
      if (typeof options.maxRetries !== "number" || options.maxRetries < 0) {
        throw new Error(
          `Invalid maxRetries: ${options.maxRetries}. Must be a non-negative number.`,
        );
      }
    }
  }

  /**
   * Throw error if builder has already been built.
   *
   * @throws Error if builder has already been built
   */
  private throwIfBuilt(): void {
    if (this.isBuilt) {
      throw new Error(
        "Builder has already been built. Create a new builder to configure another proxy.",
      );
    }
  }
}
